/**
 * actionSlice — read the single action out of a launch value object.
 *
 * This is the surviving half of `action-list-contract`'s interim launch
 * builder, which `prefill-drawer` retired once
 * `buildLaunchPayload` + `splitLaunchValues` / `mergeLaunchSlice` were wired
 * through the drawer. The assembler, the mixed-patch splitter and the
 * `schemaHasActionList` switch all went with it — each has a
 * declared-schema-driven replacement (`buildLaunchPayload`,
 * `LaunchForm.writePatch`, `buildLaunchBinding(...).isReshaped`). This accessor
 * did not, because two BINDING-FREE helpers still need it.
 *
 * ⚠️ Why it is not `mergeLaunchSlice`. That is the general reader and every
 * caller that HAS a `LaunchBinding` should use it. `addressParts` and
 * `interestTargetOptions` do not: they are handed a bare values record by
 * callers that never resolved a schema (today's flat `App.tsx` branch, the
 * moved unit suites), so they cannot ask the binding where the action list
 * lives. They look in the same two places they always have — nested first, flat
 * as the fallback — and this is the ONE place that "nested first" is spelled.
 *
 * The `"actions"` literal is therefore deliberate and scoped: it is a FALLBACK
 * probe for helpers with no binding, never the form's or the payload's source of
 * truth. Everything that reaches the wire resolves the action list by role
 * (`LAUNCH_ROLE_CANDIDATES.actions`), so a contract-side rename fails loudly
 * through `missingRoles` rather than silently here.
 *
 * Pure: no React, no Office.js, no network.
 */

/**
 * The single action inside a launch value object, or `{}`.
 *
 * Returns `{}` — never throws, never null — for a value object with no usable
 * `actions`: every caller reads optional keys off the result, and a launch must
 * never be blocked by a payload surprise.
 */
export function actionOf(
  values: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const actions = values?.actions;
  return Array.isArray(actions) && actions[0] && typeof actions[0] === "object"
    ? (actions[0] as Record<string, unknown>)
    : {};
}
